import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { tLead, tLeadUpdatedEvent } from '../../providers/SocketsProvider/Sockets.types';
import type { tLeadActiveChange } from '../../components/Chat/leadActiveChanged.event';
import { eChatLeadsTabs } from './Messenger.enum';
import { tChatLead } from './Messenger.types';

const CHANNELS: ReadonlySet<string> = new Set(Object.values(eChannelType));

/**
 * Resolves the channel integration for the deep-link fallback path in
 * Messenger.hook.tsx (when a `leadId` URL param is present but the lead is not
 * found in the initial list response).
 *
 * Priority: (1) `integration` URL query param, when it is a real channel,
 * (2) prior modal-context value. `undefined` when neither names one: the lead is
 * built with `toLeadChatModalLead`, so the access gate assigns the chat's own
 * default (WhatsApp API when the viewer can use it) before the chat mounts
 * (AGI-761). Defaulting to WhatsApp Web here opened an empty Web conversation
 * for WAPI leads, and the subscription that decides the default is not loaded
 * yet when this runs on a fresh page.
 *
 * The chat still never renders with an undefined channel, which caused the
 * WhatsApp-group length heuristic in MessageBubbleV2 to fire for TikTok handles
 * (tiktok-prod-rollout CRITICAL-1): `useChatModalGate` fills it before `ready`.
 *
 * @param integrationParam  - Value of the `?integration=` URL query param (or null/undefined if absent).
 * @param priorIntegration  - `selectedLeadForChatModal?.chat?.integration` from modal context (may be undefined).
 * @returns The known channel, or undefined for the gate to resolve.
 */
export function resolveDeepLinkIntegration(
  integrationParam: string | null | undefined,
  priorIntegration: eChannelType | undefined,
): eChannelType | undefined {
  // An unknown or empty param falls through: a URL value is never trusted as a channel unchecked.
  if (integrationParam && CHANNELS.has(integrationParam)) return integrationParam as eChannelType;
  return priorIntegration;
}

/**
 * INT-1500 U14: at most one count request per this window while lead updates
 * stream in (a throttle, not a debounce, so a busy workspace still refreshes).
 * Shared by the chat list and the funnel board (INT-1597).
 */
export const UNDELIVERED_COUNT_REFRESH_MS = 2000;

export type tUndeliveredMark = Pick<tChatLead, 'lastMessageFailed' | 'lastMessageFailedAt'>;

/**
 * INT-1500 U14: the mark a socket payload states outright, in the leads-service
 * list's snake_case or in camelCase (the lead-updated frame leads-service
 * sends when a lead's mark changes). Null when the payload says nothing.
 */
export function carriedUndeliveredMark(lead: tLead): tUndeliveredMark | null {
  const failed =
    typeof lead.last_message_failed === 'boolean'
      ? lead.last_message_failed
      : typeof lead.lastMessageFailed === 'boolean'
        ? lead.lastMessageFailed
        : null;
  if (failed === null) return null;
  const failedAt = lead.last_message_failed_at ?? lead.lastMessageFailedAt ?? null;
  return {
    lastMessageFailed: failed,
    lastMessageFailedAt: failed ? failedAt : null,
  };
}

/**
 * INT-1500 U14: leads-service answers 422 to `undelivered` (list and count)
 * for a workspace whose leads are projection-backed. There the filter does
 * not exist: the chip hides, with no zero and no error toast.
 */
export function isUndeliveredFilterRefused(error: unknown): boolean {
  const failure = error as
    | { response?: { status?: number }; status?: number }
    | null
    | undefined;
  return (failure?.response?.status ?? failure?.status) === 422;
}

/**
 * INT-1538 U7 (KTD8): the "No entregados" mark of a lead after a socket update.
 *
 * The server is the only source of the mark. A payload that carries it
 * decides; one that does not says nothing, and the row keeps what it had. The
 * client never derives it: a newer message, opening or reading the chat leave
 * it alone (the D12 rule that cleared it on any newer message is gone). Every
 * clear arrives as its own frame from leads-service's C9 listener.
 *
 * Shared by the chat list and the funnel board (INT-1597): `prevLead` is
 * whichever row or card already carried the mark.
 */
export function resolveUndeliveredMark(
  lead: tLead,
  prevLead?: tUndeliveredMark,
): tUndeliveredMark {
  const carried = carriedUndeliveredMark(lead);
  if (carried) {
    // A frame that asserts the mark without its time keeps the row's time,
    // or the lead would sink to the bottom of the failure-ordered tab.
    return carried.lastMessageFailed && !carried.lastMessageFailedAt && prevLead?.lastMessageFailed
      ? { ...carried, lastMessageFailedAt: prevLead.lastMessageFailedAt ?? null }
      : carried;
  }
  return {
    lastMessageFailed: prevLead?.lastMessageFailed ?? false,
    lastMessageFailedAt: prevLead?.lastMessageFailed
      ? prevLead.lastMessageFailedAt ?? null
      : null,
  };
}

/**
 * INT-1538 U7 (KTD7): whether the lead wrote the conversation's latest
 * message, as a frame's `message` block states it. A frame without it keeps
 * what the row had, like the mark.
 */
function resolveLastMessageFromLead(
  lead: tLead,
  prevLead?: tChatLead,
): boolean | undefined {
  const carried = lead.message?.last_message_from_lead;
  return typeof carried === 'boolean'
    ? carried
    : prevLead?.chat?.lastMessageFromLead;
}

type tFailureKey = Pick<tChatLead, 'lastMessageFailedAt' | 'numericId'>;

/** Epoch ms of a lead's `lastMessageFailedAt`, or null when unmarked or unreadable. */
function failedInstant(lead: tFailureKey): number | null {
  if (!lead.lastMessageFailedAt) return null;
  const instant = new Date(lead.lastMessageFailedAt).getTime();
  return Number.isNaN(instant) ? null : instant;
}

/**
 * The server's order for "No entregados": `last_message_failed_at DESC,
 * id DESC` on the numeric id. Negative when `a` goes first, positive when `b`
 * does, 0 when they cannot be told apart (an unreadable time, or a tie with
 * an id missing).
 */
function compareFailureOrder(a: tFailureKey, b: tFailureKey): number {
  const atA = failedInstant(a);
  const atB = failedInstant(b);
  if (atA === null || atB === null) return 0;
  if (atA !== atB) return atB - atA;
  if (!a.numericId || !b.numericId) return 0;
  return b.numericId - a.numericId;
}

/**
 * INT-1538 U7 (KTD9): where a lead updated by a frame goes in the
 * "No entregados" tab, which is ordered like its list: newest failure first,
 * ties by numeric id, newest first.
 *
 * - Unmarked, or archived (`active === false`): out of the tab, at once.
 * - Marked: placed by that order, never raised by activity. Rows it cannot
 *   tell apart from it (a tie with an id unknown) stay ahead of it.
 * - `oldestLoaded` is the last row the server paged to. While more pages
 *   exist, a lead that sorts after it belongs to a page not yet loaded: it
 *   is left for that page instead of being shown out of order (or twice).
 */
export function placeInUndeliveredTab(
  leads: tChatLead[],
  updated: tChatLead,
  oldestLoaded: tFailureKey | null,
  isLastPage: boolean,
): tChatLead[] {
  const others = leads.filter((lead) => lead.id !== updated.id);
  if (!updated.lastMessageFailed || updated.active === false) return others;

  if (!isLastPage && oldestLoaded && compareFailureOrder(updated, oldestLoaded) > 0) {
    return others;
  }

  const index =
    failedInstant(updated) === null
      ? -1
      : others.findIndex(
          (lead) => failedInstant(lead) === null || compareFailureOrder(updated, lead) < 0,
        );

  return index === -1
    ? [...others, updated]
    : [...others.slice(0, index), updated, ...others.slice(index)];
}

/**
 * BUG-1127: whether a lead with this `active` belongs to the tab. Only
 * «Activos» and «Archivados» filter on it; every other tab shows both.
 */
export function fitsActiveTab(tab: eChatLeadsTabs, active: boolean): boolean {
  if (tab === eChatLeadsTabs.ACTIVE) return active;
  if (tab === eChatLeadsTabs.ARCHIVED) return !active;
  return true;
}

export type tFrameActiveChange = tLeadActiveChange & {
  /** When the change was stored (`active_changed_at`), or null when not sent. */
  changedAt: string | null;
};

/**
 * BUG-1127: the archive or activation a `lead-updated` frame announces, or
 * null when `active` is not among its changes. A frame's top-level `active`
 * alone is a snapshot that may come from a lagging replica, never a change.
 */
export function frameActiveChange(
  lead: tLeadUpdatedEvent['response'],
): tFrameActiveChange | null {
  const changes = lead.changes;
  if (!changes || Array.isArray(changes) || typeof changes.active !== 'boolean') {
    return null;
  }
  return {
    id: lead.uuid,
    active: changes.active,
    changedAt: typeof lead.active_changed_at === 'string' ? lead.active_changed_at : null,
  };
}

/**
 * BUG-1127: a per-lead order for archive frames. leads-service consumes the
 * event on many pods with no ordering, so an older frame can arrive after a
 * newer one. The returned check is true for a frame older than the last one
 * seen for its lead (drop it), and records the time otherwise. Times are
 * fixed-width UTC strings, so they compare as strings. A frame without a time
 * is never dropped and leaves the order as it was.
 */
export function createArchiveFrameOrder(): (change: tFrameActiveChange) => boolean {
  const lastSeen = new Map<string, string>();
  return (change) => {
    if (!change.changedAt) return false;
    const previous = lastSeen.get(change.id);
    if (previous && change.changedAt < previous) return true;
    lastSeen.set(change.id, change.changedAt);
    return false;
  };
}

type tActivityKey = Pick<tChatLead, 'lastActionAt' | 'numericId'>;

/** Epoch ms of a lead's `lastActionAt`, or null when absent or unreadable. */
function activityInstant(lead: tActivityKey): number | null {
  if (!lead.lastActionAt) return null;
  const instant = new Date(lead.lastActionAt).getTime();
  return Number.isNaN(instant) ? null : instant;
}

/**
 * The list's order: `last_action_at DESC, id DESC` on the numeric id.
 * Negative when `a` goes first, positive when `b` does, 0 when they cannot be
 * told apart (an unreadable time, or a tie with an id missing).
 */
function compareActivityOrder(a: tActivityKey, b: tActivityKey): number {
  const atA = activityInstant(a);
  const atB = activityInstant(b);
  if (atA === null || atB === null) return 0;
  if (atA !== atB) return atB - atA;
  if (!a.numericId || !b.numericId) return 0;
  return b.numericId - a.numericId;
}

/**
 * BUG-1127: where a lead that moved into «Activos» or «Archivados» goes. Like
 * {@link placeInUndeliveredTab}, by the list's own order and never on top:
 * activating or archiving is not activity.
 *
 * - A lead already shown, one over the quota (the list hides those) or one
 *   without a readable time is left out: the same rows come back unchanged.
 * - Rows it cannot tell apart from it (a time unknown) stay ahead of it.
 * - While more pages exist, a lead that sorts after the last loaded row
 *   belongs to a page not loaded yet and is left for that page.
 */
export function placeInActivityOrder(
  leads: tChatLead[],
  options: { lead: tChatLead; isLastPage: boolean },
): tChatLead[] {
  const { lead, isLastPage } = options;
  if (
    lead.isQuotaExceeded ||
    activityInstant(lead) === null ||
    leads.some(({ id }) => id === lead.id)
  ) {
    return leads;
  }

  const oldestLoaded = leads[leads.length - 1];
  if (!isLastPage && oldestLoaded && compareActivityOrder(lead, oldestLoaded) > 0) {
    return leads;
  }

  const index = leads.findIndex(
    (row) => activityInstant(row) !== null && compareActivityOrder(lead, row) < 0,
  );

  return index === -1 ? [...leads, lead] : [...leads.slice(0, index), lead, ...leads.slice(index)];
}

/**
 * INT-1538 U7: a frame whose only changes are the mark itself (leads-service's
 * C9 listener). It flips the "!" in place and moves no row in any tab.
 */
export function isMarkOnlyFrame(changes: Record<string, unknown> | unknown[] | null | undefined): boolean {
  if (!changes || Array.isArray(changes)) return false;
  const keys = Object.keys(changes);
  return (
    keys.length > 0 &&
    keys.every((key) => key === 'last_message_failed' || key === 'last_message_failed_at')
  );
}

/**
 * INT-1500 U14 (contract C9): reads bff-web's
 * GET /workspace/:wid/leads/undelivered/count, `{ "count": N }` (a `data`
 * envelope is read too). Anything that is not a whole, non-negative number is
 * null: the chip hides rather than show a count it cannot back.
 */
export function parseUndeliveredCount(raw: unknown): number | null {
  const body =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  if (!body) return null;
  const data =
    typeof body.data === 'object' && body.data !== null
      ? (body.data as Record<string, unknown>)
      : null;
  const count = typeof body.count === 'number' ? body.count : data?.count;
  return typeof count === 'number' && Number.isInteger(count) && count >= 0
    ? count
    : null;
}

export function chatLeadParser(
  leadUpdatedEvent: tLead,
  prevLead?: tChatLead,
): tChatLead {
  return {
    active: leadUpdatedEvent.active,
    assigneeAuthId: leadUpdatedEvent.user?.auth_id ?? '',
    chat: {
      integration:
        eChannelType[
          leadUpdatedEvent.message?.channel_type || eChannelType.WHATSAPP_WEB
        ],
      lastMessageOn: leadUpdatedEvent.message?.last_message_on || null,
      lastMessageText: leadUpdatedEvent.message?.last_message || null,
      read: leadUpdatedEvent.message?.read ?? false,
      unreadCount: leadUpdatedEvent.message?.unread_messages || 0,
      lastMessageFromLead: resolveLastMessageFromLead(leadUpdatedEvent, prevLead),
    },
    // INT-1538: frames carry leads-service's numeric id; keep the row's otherwise.
    numericId:
      typeof leadUpdatedEvent.id === 'number' && leadUpdatedEvent.id > 0
        ? leadUpdatedEvent.id
        : prevLead?.numericId,
    // BUG-1127: the list's order key; keep the row's when the frame lacks it.
    lastActionAt: leadUpdatedEvent.last_action_at || prevLead?.lastActionAt,
    isQuotaExceeded: Boolean(leadUpdatedEvent.is_quota_exceeded),
    hasCalendarEvent: prevLead?.hasCalendarEvent || false,
    handle: leadUpdatedEvent.handle,
    id: leadUpdatedEvent.uuid,
    name: leadUpdatedEvent.full_name || '',
    profilePicture: leadUpdatedEvent.profile_picture || '',
    status: {
      id: leadUpdatedEvent.status?.uuid,
      name: leadUpdatedEvent.status?.name || '',
    },
    leadbotState: leadUpdatedEvent.leadbot_state,
    ...resolveUndeliveredMark(leadUpdatedEvent, prevLead),
  };
}
